import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { blogPosts } from "../../../constant/blogPosts";

const Blogs = () => {
  return (
    <section className="min-h-screen bg-[#f8fafc] py-20 px-6 md:px-14">
      <Helmet>
        <title>Blog - QuantaRock | Data Center &amp; IT Infrastructure Insights</title>
        <meta
          name="description"
          content="Expert insights on colocation, cloud, networking, data centers, and enterprise IT infrastructure from QuantaRock Systems."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://quantarock.in/resources/blogs" />
        <meta property="og:title" content="QuantaRock Blog" />
        <meta
          property="og:description"
          content="Expert insights on colocation, cloud, networking, data centers, and enterprise IT infrastructure."
        />
        <meta property="og:url" content="https://quantarock.in/resources/blogs" />
        <meta property="og:image" content="https://quantarock.in/og-image.jpg" />
      </Helmet>
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
        className="max-w-7xl mx-auto mb-20"
      >
        <div className="bg-white rounded-[32px] shadow-xl border border-gray-100 overflow-hidden">
          <div className="grid lg:grid-cols-2 items-center">
            {/* Left Content */}
            <div className="p-8 md:p-14">
              <span className="inline-block px-4 py-2 rounded-full bg-[var(--website-secondary-color)] text-[var(--on-secondary)] font-semibold text-sm mb-5">
                Knowledge Hub
              </span>

              <h1 className="text-[var(--website-primary-color)] text-3xl md:text-4xl font-bold leading-tight mb-6">
                Smart Insights for
                <br />
                Modern IT Infrastructure
              </h1>

              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Explore expert-written blogs covering colocation, cloud,
                networking, data centers, enterprise security, and future-ready
                digital infrastructure solutions.
              </p>

              <div className="flex flex-wrap gap-4">
                <div className="bg-gray-50 px-5 py-4 rounded-2xl border">
                  <p className="text-2xl font-bold text-black">{blogPosts.length}</p>
                  <p className="text-sm text-gray-500">Expert Articles</p>
                </div>

                <div className="bg-gray-50 px-5 py-4 rounded-2xl border">
                  <p className="text-2xl font-bold text-black">Regular</p>
                  <p className="text-sm text-gray-500">Fresh Updates</p>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="h-full bg-gradient-to-br from-[var(--website-primary-color)] to-black p-10 flex items-center justify-center">
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-8 w-full max-w-md">
                <h3 className="text-white text-2xl font-bold mb-4">
                  Latest Technology Trends
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Data Centers • Colocation • Cloud • AI Infrastructure •
                  Networking • Security • Enterprise IT
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Blog Cards */}
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 xl:grid-cols-3 gap-8">
        {blogPosts.map((post, index) => (
          <motion.div
            key={post.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.5,
              delay: index * 0.08,
            }}
            whileHover={{ y: -10 }}
            className="group bg-white rounded-[28px] border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden"
          >
            {/* Image */}
            <div className="relative bg-[#fafafa] p-6">
              <img
                src={require(`../../../assets/Blogs/${post.image}`)}
                alt={post.title}
                className="w-full h-52 object-contain transition duration-500 group-hover:scale-105"
                loading="lazy"
              />

              <div className="absolute top-5 right-5">
                {/* <div className="bg-white shadow-md rounded-full px-4 py-2 text-xs font-semibold text-gray-700">
                  New Blog
                </div> */}
              </div>
            </div>

            {/* Content */}
            <div className="p-7">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-400">{post.date}</p>
                <p className="text-sm font-medium text-gray-500">
                  {post.author}
                </p>
              </div>

              <h2 className="text-xl font-bold text-gray-800 leading-snug mb-4 group-hover:text-[var(--website-primary-color)] transition duration-300">
                {post.title}
              </h2>

              <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                {post.summary}
              </p>

              <div className="pt-6">
                <Link
                  to={`/resources/blogs/${post.id}`}
                  className="inline-flex items-center justify-center w-full py-3 rounded-xl bg-[var(--website-primary-color)] text-white font-semibold hover:bg-[var(--website-primary-color)] transition duration-300"
                >
                  Read Full Blog →
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Blogs;
